import { t } from '../lib/i18n';

/**
 * Download the CFDI XML or printed PDF for a given ticket. We fetch with
 * credentials so the 403/404/503 distinctions are visible — a plain
 * <a href> would silently navigate the user to an error page.
 *
 * Successful response is streamed into a Blob and exposed via a one-shot
 * object URL the browser saves under the filename in Content-Disposition.
 *
 * `idate` is optional (only meaningful for type='xml'); when present, the
 * server skips the YY/MM scan and goes straight to the dated path.
 */
export async function downloadInvoiceFile(
  type: 'xml' | 'pdf',
  sid: number,
  tnum: number,
  idate?: string,
): Promise<void> {
  const params = new URLSearchParams({ sid: String(sid), tnum: String(tnum) });
  if (type === 'xml' && idate) params.set('date', idate);
  const res = await fetch(`/api/invoice/${type}?${params.toString()}`, {
    credentials: 'same-origin',
  });
  if (res.status === 404) throw new Error(t.ticket.download.missing);
  if (res.status === 503) throw new Error(t.ticket.download.notConfigured);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  // Filename from header; fall back to the convention if not present.
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename="([^"]+)"/.exec(cd);
  const filename = m?.[1] ??
    (type === 'xml'
      ? `XCFDI_${String(sid).padStart(2, '0')}_${tnum}v40.xml`
      : `PDF_${String(sid).padStart(2, '0')}_${tnum}.pdf`);

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
